import { useCallback, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import UserManagerService from '../services/UserManager/userManager.service';
import { QueryKeys } from '../enums/queryKeys';
import type { UserRecord, UserSortField, UserSortOrder } from '../types/UserManagementTypes';

export const DEFAULT_SORT_FIELD: UserSortField = 'name';
export const DEFAULT_SORT_ORDER: UserSortOrder = 'ascend';

/** Single invalidation point for the create/edit/delete mutations that land later. */
export const invalidateUsersList = (queryClient: QueryClient) => queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_USERS_LIST] });

export const useUsersList = () => {
  const [sortBy, setSortBy] = useState<UserSortField>(DEFAULT_SORT_FIELD);
  const [sortOrder, setSortOrder] = useState<UserSortOrder>(DEFAULT_SORT_ORDER);

  const queryClient = useQueryClient();
  const { getUsersInfinite } = UserManagerService();
  // staleTime 0: User Manager must always show current data on open/refresh, never a cached page.
  const { data, isLoading, isLoadingError, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    ...getUsersInfinite(sortBy, sortOrder),
    staleTime: 0,
  });

  const users: UserRecord[] = data?.pages.flatMap((page) => page.users) ?? [];

  const setSort = (field: UserSortField, order: UserSortOrder) => {
    setSortBy(field);
    setSortOrder(order);
  };

  // Refetches every sort variant of the list, so callers don't need the client.
  const refreshUsers = useCallback(() => invalidateUsersList(queryClient), [queryClient]);

  return {
    users,
    refreshUsers,
    isLoadingUsers: isLoading,
    isUsersLoadingError: isLoadingError,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    sortBy,
    sortOrder,
    setSort,
  };
};
